/**
 * Strapi query parameter types and builder
 */

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface SortParams {
  field: string;
  order: 'asc' | 'desc';
}

export interface FilterParams {
  [key: string]: any;
}

export interface QueryParams {
  populate?: string | string[] | Record<string, any>;
  filters?: FilterParams;
  sort?: SortParams | SortParams[];
  pagination?: PaginationParams;
  fields?: string[];
  locale?: 'en' | 'ar';
}

export function buildStrapiQuery(params?: QueryParams): string {
  if (!params) return '';

  const queryParts: string[] = [];

  // Populate
  if (params.populate) {
    if (params.populate === '*') {
      queryParts.push('populate=*');
    } else if (typeof params.populate === 'string') {
      queryParts.push(`populate=${params.populate}`);
    } else if (Array.isArray(params.populate)) {
      params.populate.forEach((field, index) => {
        queryParts.push(`populate[${index}]=${field}`);
      });
    } else if (typeof params.populate === 'object') {
      // Complex populate with nested fields
      Object.keys(params.populate).forEach(key => {
        const populateObj = params.populate as Record<string, any>;
        const value = populateObj[key];
        if (Array.isArray(value)) {
          value.forEach((field, index) => {
            queryParts.push(`populate[${key}][fields][${index}]=${field}`);
          });
        } else {
          queryParts.push(`populate[${key}]=${value}`);
        }
      });
    }
  }

  // Filters
  if (params.filters) {
    Object.keys(params.filters).forEach(key => {
      const value = params.filters![key];
      if (typeof value === 'object' && value !== null) {
        // Complex filter (e.g., { $eq: 'value' })
        Object.keys(value).forEach(operator => {
          queryParts.push(`filters[${key}][${operator}]=${value[operator]}`);
        });
      } else {
        // Simple filter
        queryParts.push(`filters[${key}]=${value}`);
      }
    });
  }

  // Sort
  if (params.sort) {
    if (Array.isArray(params.sort)) {
      params.sort.forEach((sortItem, index) => {
        queryParts.push(`sort[${index}]=${sortItem.field}:${sortItem.order}`);
      });
    } else {
      queryParts.push(`sort[0]=${params.sort.field}:${params.sort.order}`);
    }
  }

  // Pagination
  if (params.pagination) {
    if (params.pagination.page !== undefined) {
      queryParts.push(`pagination[page]=${params.pagination.page}`);
    }
    if (params.pagination.pageSize !== undefined) {
      queryParts.push(`pagination[pageSize]=${params.pagination.pageSize}`);
    }
  }

  // Fields
  if (params.fields && params.fields.length > 0) {
    params.fields.forEach((field, index) => {
      queryParts.push(`fields[${index}]=${field}`);
    });
  }

  // Locale
  if (params.locale) {
    queryParts.push(`locale=${params.locale}`);
  }

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}
